import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import '@maersk-global/mds-components-core/mc-textarea';
import '@maersk-global/mds-components-core/mc-tag';
import { Subscription, timer } from 'rxjs';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-aems-multiline-tag-input',
  imports: [CommonModule, FormsModule],
  templateUrl: './aems-multiline-tag-input.component.html',
  styleUrl: './aems-multiline-tag-input.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: AemsMultilineTagInputComponent,
    },
  ],
})
export class AemsMultilineTagInputComponent implements ControlValueAccessor {
  @ViewChild('dummyTextarea') dummyTextarea!: ElementRef;
  //@ViewChild('actualInput') actualInput!: ElementRef;
  @Input() label: string = '';
  @Input() placeholder: string = '';
  @Input({ required: true }) regex!: RegExp;
  @Input() heightInPixels: number = 100;
  @Input() allowDuplicates: boolean = false;
  @Input() transformToUppercase: boolean = false;
  @Output() enteredValues = new EventEmitter<string[]>();
  inputInError: boolean = false;
  longPressThreshold: number = 1000;
  timer: Subscription | null = null;
  arraryOfTags = signal<string[]>([]);
  currentInput: string = '';
  touched: boolean = false;
  disabled: boolean = true;
  inputValue: string = '';
  invalidContentPasted = signal<boolean>(false);
  constructor() {}

  onBlur(event: FocusEvent) {
    const element = event.target as HTMLInputElement;
    if (element.value) {
      this.validateAndAddTag(element.value, element);
    }
    this.onchange(this.arraryOfTags());
  }

  onEnterPressed(event: Event) {
    const element = event.target as HTMLInputElement;
    if (element.value) {
      this.validateAndAddTag(element.value, element);
      this.onchange(this.arraryOfTags());
    }
  }

  onBackspace(event: Event) {
    this.inputInError = false;
    const element = event.target as HTMLInputElement;
    if (!element.value) {
      this.arraryOfTags.update((values) => {
        return values.filter((_, i) => i !== values.length - 1);
      });
      this.onchange(this.arraryOfTags());
      if (!this.timer) {
        this.timer = timer(this.longPressThreshold).subscribe(() => {
          this.arraryOfTags.set([]);
          this.timer = null;
        });
      }
    }
  }
  onContentPasted(event: ClipboardEvent) {
    const clipboardData = event.clipboardData;
    if (clipboardData) {
      const pastedText = clipboardData.getData('text');
      const matches = pastedText.match(this.regex);
      if (matches) {
        this.arraryOfTags.update((values) => {
          return [...values, ...matches];
        });
        if (!this.allowDuplicates) {
          this.arraryOfTags.update((values) => {
            return [...new Set(values)];
          });
        }
        this.onchange(this.arraryOfTags());
      } else {
        this.invalidContentPasted.set(true);
        timer(2000).subscribe(() => this.invalidContentPasted.set(false));
      }
    }
    event.preventDefault();
  }
  onKeyUp(_: KeyboardEvent) {
    if (!this.touched) {
      this.ontouched();
      this.touched = true;
    }
    if (this.timer) {
      this.timer.unsubscribe();
      this.timer = null;
    }
  }

  onTagCloseClicked(index: number) {
    this.arraryOfTags.update((values) => {
      return values.filter((_, i) => i !== index);
    });
    this.onchange(this.arraryOfTags());
  }
  arrowKeyPressed() {
    this.dummyTextarea.nativeElement.focus();
  }

  private validateAndAddTag(tag: string, element: HTMLInputElement) {
    if (tag.match(this.regex)) {
      if (this.transformToUppercase) {
        tag = tag.toUpperCase();
      }
      if (!this.allowDuplicates && this.arraryOfTags().includes(tag)) {
        this.inputInError = true;
        return;
      }
      this.arraryOfTags.update((values) => {
        return [...values, tag];
      });
      this.inputInError = false;
      element.value = '';
    } else {
      this.inputInError = true;
    }
  }
  onchange = (_: string[]) => {};
  ontouched = () => {};

  writeValue(value: string[] | null): void {
    const safeValue = value ?? [];
    if (!this.allowDuplicates) {
      this.arraryOfTags.set([...new Set(safeValue)]);
    } else {
      this.arraryOfTags.set(safeValue);
    }
    this.inputValue = '';
    this.inputInError = false;
  }
  registerOnChange(fn: any): void {
    this.onchange = fn;
  }

  registerOnTouched(fn: any): void {
    this.ontouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
